/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useRef} from 'react';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import Graphic from '@arcgis/core/Graphic';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import debounce from 'lodash.debounce';
import {GenericObject, HVSRecord} from '@ion/core/src/types/entities';
import {hexToRgb} from '@ion/core/src/utils/StringUtil';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {
  BACKGROUND_NODE_LAYER,
  BACKGROUND_PATH_LAYER,
  BACKGROUND_WORLD_LAYER,
  END_EVENT_NODE_LAYER,
  HISTORY_FIELDS,
  HISTORY_TRIP_SERVICE_FIELDS,
  IN_TRIP_NODE_LAYER,
  IN_TRIP_PATH_LAYER,
  TRIP_ASSET_LAYER_ID
} from '../constants';
import {
  getAssetHistoryColorIndex,
  registerAssetHistoryColors,
  startAssetHistoryColors
} from '../helpers/assetHistoryColors';
import {
  createEndNodeRenderer,
  createNodeRenderer,
  createUniqueValuePathRenderer,
  getIconSizeByZoom,
  getIconSizeLabelByZoom,
  getLayerById,
  getLayerRenderer,
  hasOffsetIcon,
  IconZoomThreshold,
  layerExists,
  removeLayers,
  RendererMapType
} from '../helpers/MapViewUtils';
import useAssetHistoryController from './useAssetHistoryController';

type CreateFeatureLayerProps = {
  name: string;
  source: any[];
  renderer?: any;
  opacity?: number;
  isTripServicesEnabled?: boolean;
};

type AssetHistoryMapLayersProps = {
  map: __esri.Map;
  mapView: __esri.MapView;
  zoom: number;
  isSelectionEnabled: boolean;
  onLayerDrawn: Function;
  featureFilter: GenericObject;
  mapPannedRef: React.MutableRefObject<boolean>;
};

/**
 * Custom hook for managing map layers in the Asset History Report (AHR) component.
 *
 * This hook encapsulates the logic for creating, updating, and managing various
 * map layers used in the AHR, including:
 * - Node layers (in-trip, background, end events)
 * - Path layers (in-trip, background)
 * - Background masking layer (when selection is enabled)
 * - Graphic layers for asset rendering
 *
 * It leverages refs to maintain mutable state and avoid unnecessary re-renders,
 * ensuring efficient updates as map properties (zoom, filters, etc.) change.
 *
 * Key Responsibilities:
 * - Initializes and manages layer refs for consistent access.
 * - Handles dynamic renderer creation based on zoom level and asset data.
 * - Applies filters to relevant layer views for synchronized display.
 * - Manages background masking layer visibility during selection.
 * - Binds layer view events for post-render actions (e.g., hover, click).
 * - Ensures proper layer ordering and cleanup on unmount.
 *
 * @param map - The ArcGIS Map instance.
 * @param mapView - The ArcGIS MapView instance.
 * @param zoom - Current zoom level of the map.
 * @param isSelectionEnabled - Flag indicating if selection mode is active.
 * @param onLayerDrawn - Callback executed when a layer finishes drawing.
 * @param featureFilter - Filter applied to layer features.
 * @param mapPannedRef - Ref tracking whether the map has been panned.
 *
 * @returns Object containing refs and utility functions for layer management.
 */
const useAssetHistoryMapLayers = ({
  map,
  mapView,
  zoom,
  isSelectionEnabled,
  onLayerDrawn,
  featureFilter,
  mapPannedRef
}: AssetHistoryMapLayersProps) => {
  const inTripNodeLayerRef            = useRef(null);
  // the color list position each asset keeps for the report - shared with
  // the History grid, so an asset's row and its drawing take the same color,
  // and handed out the first time the asset is drawn so it stays put as more
  // assets load ...
  const colorIndexOf = getAssetHistoryColorIndex;
  const backgroundNodeLayerRef        = useRef(null);
  const endEventNodeLayerRef          = useRef(null);
  const inTripPathlayerRef            = useRef(null);
  const backgroundPathLayerRef        = useRef(null);
  const normalIconSizeRendererRef     = useRef(null);
  const mediumIconSizeRendererRef     = useRef(null);
  const largeIconSizeRendererRef      = useRef(null);
  const endEventNodeRendererRef       = useRef(null);
  const endEventNodeOffsetRendererRef = useRef(null);
  const backgroundWorldLayerRef       = useRef(null);
  const assetsPointsRef               = useRef(null);
  const graphicLayerRef               = useRef(null);
  const preventZoomRef                = useRef(false);
  const iconSizeLabelRef              = useRef<string | null>(null);
  const {
    safeBind,
    moveMapView,
    addGeometry,
    opacityLayer,
    createMarker,
    generateAssetMap,
    applyEditsToLayer,
    resetFeaturesLayer,
    onEndEventNodeDrawn,
    onBackgroundPathDrawn,
    onBackgroundNodeDrawn,
    onInTripPathDrawn,
    onInTripNodeDrawn,
    inTripNodeViewLayerRef,
    endEventNodeViewLayerRef,
    backgroundNodeViewLayerRef
  } = useAssetHistoryController({
    map,
    mapView,
    zoom,
    mapPannedRef,
    onLayerDrawn,
    featureFilter,
    isSelectionEnabled
  });

  /**
   * Applies a given filter to multiple map layer views.
   *
   * This function updates the `filter` property of several layer views:
   * - `inTripNodeViewLayerRef`
   * - `backgroundNodeViewLayerRef`
   * - `endEventNodeViewLayerRef`
   *
   * It ensures that the same filter is consistently applied across these layers,
   * enabling synchronized filtering behavior for related map features.
   *
   * @param featureFilter - The filter to apply to the layer views.
   */
  const applyFilterToLayerViews = useCallback(featureFilter => {
    if (inTripNodeViewLayerRef.current) {
      inTripNodeViewLayerRef.current.filter = featureFilter;
    }
    if (backgroundNodeViewLayerRef.current) {
      backgroundNodeViewLayerRef.current.filter = featureFilter;
    }
    if (endEventNodeViewLayerRef.current) {
      endEventNodeViewLayerRef.current.filter = featureFilter;
    }
  }, [inTripNodeViewLayerRef.current, backgroundNodeViewLayerRef.current, endEventNodeViewLayerRef.current]);

  /**
   * Creates a FeatureLayer instance configured with the provided data and rendering options.
   *
   * This function builds a client-side FeatureLayer using the given source graphics,
   * applies the specified renderer, and sets common configuration such as fields,
   * object ID, and opacity.
   *
   * It is memoized to avoid unnecessary re-creation unless HISTORY_FIELDS changes.
   *
   * @param name - Unique identifier and title for the layer.
   * @param source - Collection of graphics used as the layer's data source.
   * @param renderer - Renderer used to control the visual representation of features.
   * @param opacity - Optional opacity level (default is 1).
   *
   * @returns A configured FeatureLayer instance.
   */
  const createFeatureLayer = useCallback(async({
    name,
    source,
    renderer,
    opacity = 1,
    isTripServicesEnabled = false
  }: CreateFeatureLayerProps) => {
    const featureLayer = new FeatureLayer({
      title         : name,
      id            : name,
      source        : source,
      fields        : !isTripServicesEnabled ? HISTORY_FIELDS : HISTORY_TRIP_SERVICE_FIELDS,
      objectIdField : 'OBJECTID',
      outFields     : ['*'],
      renderer,
      opacity
    });
          
    return featureLayer;
  }, [HISTORY_FIELDS, HISTORY_TRIP_SERVICE_FIELDS]);

  /**
   * Resets the graphics layer used for trip/asset rendering.
   *
   * - If the layer already exists, it clears all existing graphics to ensure a fresh state.
   * - If the layer does not exist, it initializes a new GraphicsLayer, assigns the
   *   TRIP_ASSET_LAYER_ID, and adds it to the map.
   * - When live history updates or tracking mode is enabled, it updates the click
   *   interaction setting to target this layer.
   *
   * @param setClickSetting - Function used to configure which layer handles click interactions.
   * @param historyUpdatesLive - Indicates whether live history updates are active.
   * @param isTracking - Indicates whether tracking mode is enabled.
   */
  const resetGraphicsLayer = useCallback((
    setClickSetting: Function,
    historyUpdatesLive: boolean,
    isTracking: boolean
  ) => {
    if (graphicLayerRef.current) {
      (graphicLayerRef.current as any).graphics.removeAll();
    } else {
      graphicLayerRef.current = new GraphicsLayer({
        id : TRIP_ASSET_LAYER_ID
      }) as any;
      graphicLayerRef.current && map.add(graphicLayerRef.current);
    }
    if (historyUpdatesLive || isTracking) {
      setClickSetting(TRIP_ASSET_LAYER_ID);
    }
  }, [TRIP_ASSET_LAYER_ID]);

  /**
   * Maps assets into ArcGIS graphics (Point geometry + identifying attributes).
   * This is a full transformation and does not preserve previous graphics.
   */
  const createGraphicsFromAssets = useCallback(assets => {
    return {
      graphics : assets.map(
        ({longitude, latitude, OBJECTID, messageUuid}) => ({
          attributes : {OBJECTID, messageUuid},
          geometry   : new Point({
            longitude,
            latitude
          })
        })
      )
    };
  }, []);

  /**
   * Creates and adds a background masking layer to the map when selection is enabled.
   *
   * This layer renders a semi-transparent polygon covering the entire world extent,
   * effectively dimming the map (30% black opacity) to highlight selectable elements.
   *
   * The layer is lazily initialized and stored in a ref to ensure it is created only once.
   * It is hidden by default and can be toggled visible when needed.
   *
   * @param isSelectionEnabled - Determines whether the background layer should be created.
   */
  const createBackgroundLayer = useCallback((isSelectionEnabled: boolean) => {
    if (!backgroundWorldLayerRef.current && isSelectionEnabled) {
      const mapBackgroundMask = new Polygon({
        rings : [[
          [-180, -90],
          [-180, 90],
          [180, 90],
          [180, -90],
          [-180, -90]
        ]]
      });
    
      const blackRgb = hexToRgb(dlsColor('black'));
    
      const backgroundMaskGraphic = new Graphic({
        geometry : mapBackgroundMask,
        symbol   : {
          type    : 'simple-fill',
          color   : [...blackRgb, 0.3], //30% opaque
          outline : null
        }
      });
    
      backgroundWorldLayerRef.current = new GraphicsLayer({
        id       : BACKGROUND_WORLD_LAYER,
        name     : BACKGROUND_WORLD_LAYER,
        graphics : [backgroundMaskGraphic],
        visible  : false
      }) as any;
      
      if (backgroundWorldLayerRef.current) {
        map?.layers.add(backgroundWorldLayerRef.current, 1);
      }
    }
  }, [BACKGROUND_WORLD_LAYER]);

  /**
   * Creates and caches layer renderers for different zoom levels.
   *
   * Based on the current zoom thresholds, this function initializes three renderer
   * variants (normal, medium, large) with progressively larger icon sizes.
   *
   * - Normal: Default icon size without offset.
   * - Medium: Slightly larger icons with offset applied to avoid overlap.
   * - Large: Largest icons with offset for better visibility at higher zoom levels.
   *
   * Each renderer is stored in a ref to prevent unnecessary re-creation and
   * improve rendering performance when zoom levels change.
   *
   * @param assetsMap - Collection of asset records used to build renderers.
   * @param isAssetHistory - Indicates whether the data represents asset history.
   */
  const createLayerRenderersByZoom = useCallback((assetsMap: HVSRecord[], isAssetHistory: boolean) => {
    normalIconSizeRendererRef.current = createNodeRenderer({
      assetsMap,
      isAssetHistory,
      colorIndexOf,
      inTrip   : true,
      iconSize : getIconSizeByZoom(IconZoomThreshold.Normal as number)
    }) as any;
    
    mediumIconSizeRendererRef.current = createNodeRenderer({
      assetsMap,
      isAssetHistory,
      colorIndexOf,
      inTrip   : true,
      offset   : true,
      iconSize : getIconSizeByZoom(IconZoomThreshold.Normal + 1 as number)
    }) as any;
    
    largeIconSizeRendererRef.current = createNodeRenderer({
      assetsMap,
      isAssetHistory,
      colorIndexOf,
      inTrip   : true,
      offset   : true,
      iconSize : getIconSizeByZoom(IconZoomThreshold.Large + 1 as number)
    }) as any;
  }, [createNodeRenderer, IconZoomThreshold, getIconSizeByZoom]);

  type CreateAHRLayersProps = {
    mapView: __esri.MapView;
    assetsMap: HVSRecord[];
    assets: HVSRecord[];
    nodeGraphic: any[];
    pathGraphic: any[];
    boundary: any[];
    endEventNodes: any[];
    zoomLevel?: number;
    isAssetHistory: boolean;
    setClickSetting: Function;
    safeBind: Function;
    applyReportFilter: Function;
    isTripServicesEnabled?: boolean;
    hoveredEventRef: React.MutableRefObject<boolean>;
    historyUpdatesLiveRef: React.MutableRefObject<boolean>;
  };

  /**
   * Creates and adds all Asset History Report (AHR) layers to the map.
   *
   * This function dynamically builds node, path, and end-event layers based on the
   * provided graphics and map zoom level. It also configures renderers (including
   * icon size and offset), applies background opacity, and ensures proper layer
   * ordering and event binding.
   *
   * Key responsibilities:
   * - Generate renderers based on zoom (icon size, offset, labels).
   * - Create in-trip and background layers for nodes and paths.
   * - Create end-event node layers with appropriate renderer variants.
   * - Add all valid layers to the map in a single batch.
   * - Initialize the background mask layer when selection is enabled.
   * - Bind layer view creation events safely for post-render handling.
   * - Reset layer order to maintain correct visual stacking.
   * - Configure clickable layers for interaction.
   *
   * Layers created (if data is available):
   *  - IN_TRIP_NODE_LAYER
   *  - BACKGROUND_NODE_LAYER
   *  - IN_TRIP_PATH_LAYER
   *  - BACKGROUND_PATH_LAYER
   *  - END_EVENT_NODE_LAYER
   *
   * @param mapView - Current map view instance (used for zoom-based calculations).
   * @param safeBind - Utility to safely bind layer view events.
   * @param assetsMap - Asset data used for rendering logic.
   * @param assets - raw Asset data.
   * @param boundary - Boundary data.
   * @param nodeGraphic - Graphics representing asset nodes.
   * @param pathGraphic - Graphics representing asset paths.
   * @param endEventNodes - Graphics for end-event nodes.
   * @param isAssetHistory - Indicates whether the data represents asset history.
   * @param setClickSetting - Callback to configure clickable layers.
   * @param applyReportFilter - Callback to apply Filters.
   * @param hoveredEventRef - Hover event Ref.
   * @param historyUpdatesLiveRef - Flag for History Updates Ref.
   */
  const createAHRLayers = useCallback(async({
    mapView,
    safeBind,
    assetsMap,
    assets,
    boundary,
    nodeGraphic,
    pathGraphic,
    endEventNodes,
    isAssetHistory,
    setClickSetting,
    applyReportFilter,
    hoveredEventRef,
    historyUpdatesLiveRef,
    isTripServicesEnabled = false
  }: CreateAHRLayersProps) => {
    const mapViewZoom        = mapView?.zoom as number;
    const pathLinesRenderer  = createUniqueValuePathRenderer(assetsMap, isTripServicesEnabled, colorIndexOf);
    const iconSize           = getIconSizeByZoom(mapViewZoom);
    const isOffsetIcon       = hasOffsetIcon(mapViewZoom);
    iconSizeLabelRef.current = getIconSizeLabelByZoom(mapViewZoom);

    const layersToAdd: __esri.FeatureLayer[] = [];
    inTripNodeLayerRef.current            = null;
    backgroundNodeLayerRef.current        = null;
    inTripPathlayerRef.current            = null;
    backgroundPathLayerRef.current        = null;
    endEventNodeLayerRef.current          = null;
    endEventNodeRendererRef.current       = null;
    endEventNodeOffsetRendererRef.current = null;

    if (nodeGraphic?.length) {
      inTripNodeLayerRef.current = await createFeatureLayer({
        name     : IN_TRIP_NODE_LAYER,
        source   : nodeGraphic,
        renderer : createNodeRenderer({
          inTrip : true,
          offset : isOffsetIcon,
          assetsMap,
          isAssetHistory,
          iconSize,
          colorIndexOf
        }),
        isTripServicesEnabled
      }) as any;
    
      backgroundNodeLayerRef.current = await createFeatureLayer({
        name     : BACKGROUND_NODE_LAYER,
        source   : nodeGraphic,
        renderer : createNodeRenderer({
          inTrip : false,
          offset : isOffsetIcon,
          assetsMap,
          isAssetHistory,
          iconSize,
          colorIndexOf
        }),
        opacity : opacityLayer.backgroundNodeLayer,
        isTripServicesEnabled
      }) as any;
    }

    if (pathGraphic?.length) {
      createPathLayers(pathGraphic, pathLinesRenderer, isTripServicesEnabled);
    }

    if (endEventNodes?.length) {
      endEventNodeRendererRef.current       = createEndNodeRenderer(assetsMap, isAssetHistory, {colorIndexOf}) as any;
      endEventNodeOffsetRendererRef.current = createEndNodeRenderer(assetsMap, isAssetHistory, {
        offset : true,
        colorIndexOf
      }) as any;
      endEventNodeLayerRef.current          = await createFeatureLayer({
        name     : END_EVENT_NODE_LAYER,
        source   : endEventNodes,
        renderer : createEndNodeRenderer(assetsMap, isAssetHistory, {offset: isOffsetIcon, colorIndexOf}),
        isTripServicesEnabled
      }) as any;
    }

    [
      inTripNodeLayerRef.current,
      backgroundNodeLayerRef.current,
      endEventNodeLayerRef.current
    ].filter(Boolean).forEach(layer => layer && layersToAdd.push(layer));

    if (layersToAdd?.length > 0) {
      removeLayers(map,
        [
          IN_TRIP_NODE_LAYER,
          IN_TRIP_PATH_LAYER,
          BACKGROUND_NODE_LAYER,
          BACKGROUND_PATH_LAYER,
          END_EVENT_NODE_LAYER
        ]
      );

      map?.layers.addMany(layersToAdd);
      createBackgroundLayer(isSelectionEnabled);

      safeBind(inTripNodeLayerRef.current, 'layerview-create', onInTripNodeDrawn);
      safeBind(backgroundNodeLayerRef.current, 'layerview-create', onBackgroundNodeDrawn);
      safeBind(endEventNodeLayerRef.current, 'layerview-create', onEndEventNodeDrawn);

      resetFeaturesLayer();
      setClickSetting([IN_TRIP_NODE_LAYER, BACKGROUND_NODE_LAYER]);
    }

    assetsPointsRef.current = createGraphicsFromAssets(assets) as any;
  
    const geometry = assets.map(({longitude, latitude}) => [longitude, latitude]);
    if (!boundary?.length && !preventZoomRef.current && !isCurrentTheme()) {
      moveMapView(geometry, mapPannedRef);
    }
    applyReportFilter(hoveredEventRef.current, historyUpdatesLiveRef.current, true);
  }, [
    IN_TRIP_NODE_LAYER,
    IN_TRIP_PATH_LAYER,
    END_EVENT_NODE_LAYER,
    BACKGROUND_NODE_LAYER,
    BACKGROUND_PATH_LAYER
  ]);

  type UpdateAHRLayersProps = {
    mapView: __esri.MapView;
    assetsMap: HVSRecord[];
    nodeGraphic: any[];
    pathGraphic: any[];
    endEventNodes: any[];
    allAssetsHistory: HVSRecord[];
    isAssetHistory: boolean;
    isTripServicesEnabled?: boolean;
  };

  const updateAHRLayers = useCallback(({
    mapView,
    assetsMap,
    nodeGraphic,
    pathGraphic,
    endEventNodes,
    isAssetHistory,
    allAssetsHistory,
    isTripServicesEnabled = false
  }: UpdateAHRLayersProps) => {
    const mapViewZoom        = mapView?.zoom as number;
    const pathLinesRenderer  = createUniqueValuePathRenderer(assetsMap, isTripServicesEnabled, colorIndexOf);
    const iconSize           = getIconSizeByZoom(mapViewZoom);
    const isOffsetIcon       = hasOffsetIcon(mapViewZoom);

    if (nodeGraphic?.length && inTripNodeLayerRef.current && backgroundNodeLayerRef.current) {
      inTripNodeLayerRef.current.renderer = createNodeRenderer({
        inTrip : true,
        assetsMap,
        isAssetHistory,
        iconSize,
        offset : isOffsetIcon,
        colorIndexOf
      });
      applyEditsToLayer(inTripNodeLayerRef.current, {addFeatures: nodeGraphic});
    
      backgroundNodeLayerRef.current.renderer = createNodeRenderer({
        inTrip : false,
        assetsMap,
        isAssetHistory,
        iconSize,
        offset : isOffsetIcon,
        colorIndexOf
      });
      applyEditsToLayer(backgroundNodeLayerRef.current, {addFeatures: nodeGraphic});
    }

    if (pathGraphic?.length) {
      if (inTripPathlayerRef.current && backgroundPathLayerRef.current) {
        inTripPathlayerRef.current.renderer = pathLinesRenderer;
        applyEditsToLayer(inTripPathlayerRef.current, {addFeatures: pathGraphic});
        backgroundPathLayerRef.current.renderer = pathLinesRenderer;
        applyEditsToLayer(backgroundPathLayerRef.current, {addFeatures: pathGraphic});
      } else {
        createPathLayers(pathGraphic, pathLinesRenderer, isTripServicesEnabled);
      }
    }
    
    if (endEventNodes?.length && endEventNodeLayerRef.current) {
      endEventNodeRendererRef.current       = createEndNodeRenderer(assetsMap, isAssetHistory, {colorIndexOf}) as any;
      endEventNodeOffsetRendererRef.current = createEndNodeRenderer(assetsMap, isAssetHistory, {
        offset : true,
        colorIndexOf
      }) as any;
      endEventNodeLayerRef.current.renderer = createEndNodeRenderer(assetsMap, isAssetHistory, {
        offset : isOffsetIcon,
        colorIndexOf
      });
      applyEditsToLayer(endEventNodeLayerRef.current, {addFeatures: endEventNodes});
    }
    
    assetsPointsRef.current = createGraphicsFromAssets(allAssetsHistory) as any;
  }, []);

  /**
   * Creates and adds the in-trip and background path layers to the map using the
   * provided path graphics and renderer. Both layers share the same source data,
   * while the background layer is rendered with reduced opacity to provide context.
   *
   * Once added to the map, layer view creation events are bound to trigger draw
   * callbacks, and the feature layers are reset to refresh their state.
   */
  const createPathLayers = useCallback(async(
    pathGraphic,
    pathLinesRenderer,
    isTripServicesEnabled
  ) => {
    if (pathGraphic?.length) {
      inTripPathlayerRef.current = await createFeatureLayer({
        name     : IN_TRIP_PATH_LAYER,
        source   : pathGraphic,
        renderer : pathLinesRenderer,
        isTripServicesEnabled
      }) as any;
          
      backgroundPathLayerRef.current = await createFeatureLayer({
        name     : BACKGROUND_PATH_LAYER,
        source   : pathGraphic,
        renderer : pathLinesRenderer,
        opacity  : opacityLayer.backgroundPathLayer,
        isTripServicesEnabled
      }) as any;

      map?.layers.addMany([inTripPathlayerRef.current, backgroundPathLayerRef.current]);

      safeBind(inTripPathlayerRef.current, 'layerview-create', onInTripPathDrawn);
      safeBind(backgroundPathLayerRef.current, 'layerview-create', onBackgroundPathDrawn);
      resetFeaturesLayer();
    }
  }, [map, createFeatureLayer, onInTripPathDrawn, resetFeaturesLayer]);

  type InitializeAssetsOnMapProps = {
    map: __esri.Map;
    mapView: __esri.MapView;
    assets: HVSRecord[];
    allAssetsHistory: HVSRecord[];
    routesHistory: GenericObject;
    allRoutesHistory: GenericObject;
    boundary: any[];
    zoomLevel: number;
    setZoom: Function;
    setClickSetting: Function;
    historyUpdatesLive: boolean;
    isTracking: boolean;
    isAssetHistory: boolean;
    isTripServicesEnabled?: boolean;
    isAssetSnapToRoadEnabled: boolean;
    applyReportFilter: Function;
    hoveredEventRef: React.MutableRefObject<boolean>;
    historyUpdatesLiveRef: React.MutableRefObject<boolean>;
  };

  /**
   * Initializes and renders assets on the map based on current state and configuration.
   *
   * This is the main orchestration function for map rendering that handles both initial
   * load and update scenarios. It processes asset data and creates appropriate map
   * layers and graphics based on the current mode (live tracking vs historical playback).
   *
   * Processing Flow:
   *
   * 1. Asset Validation:
   *    - Checks if assets array exists and has content.
   *    - Clears existing graphics and exits early if no assets are provided.
   *
   * 2. Asset Map Generation:
   *    - Creates a comprehensive asset map with nodes, paths, and event data.
   *    - Applies route matching (snap-to-road) when enabled.
   *    - Generates graphics for nodes, paths, and trip end events.
   *
   * 3. Live/Tracking Mode Handling:
   *    - Bypasses complex layer creation for performance.
   *    - Creates simple markers directly on the graphics layer.
   *    - Optimized for real-time asset position updates.
   *
   * 4. First Load Initialization:
   *    - Sets initial zoom level and resets pan state.
   *    - Manages background world layer visibility based on selection mode.
   *    - Resets graphics layer and configures click settings.
   *    - Creates zoom-level appropriate renderers for performance.
   *    - Builds and adds all Asset History Report (AHR) layers to the map.
   *    - Disables zoom prevention after initialization.
   *
   * 5. Update Flow (Subsequent Loads):
   *    - Generates asset map using complete historical data.
   *    - Updates existing layer renderers based on current zoom level.
   *    - Refreshes AHR layers with new asset and graphic data.
   *    - Maintains existing map state and user interactions.
   *
   * Layer Management:
   * - Creates and manages specialized layers for different data types.
   * - Handles layer visibility and interaction states.
   * - Optimizes rendering based on zoom level and data complexity.
   *
   * Performance Considerations:
   * - Differentiates between first load and updates for optimization.
   * - Uses appropriate rendering strategies based on data volume.
   * - Manages memory by clearing unused graphics and layers.
   *
   * @param map Current Map instance for layer and graphics management.
   * @param mapView Current MapView instance for spatial operations and rendering.
   * @param assets Raw asset data array to be processed and rendered on the map.
   * @param setZoom Callback function to update the map's zoom level state.
   * @param boundary Boundary geometry data used for spatial filtering and path rendering.
   * @param zoomLevel Initial zoom level to apply during first load initialization.
   * @param isTracking Boolean flag indicating if the map is in live tracking mode.
   * @param routesHistory Cached route matching results for snap-to-road functionality.
   * @param isAssetHistory Boolean flag indicating historical playback mode vs live mode.
   * @param setClickSetting Callback to configure clickable layer interactions and behaviors.
   * @param hoveredEventRef React ref storing the currently hovered event for highlighting.
   * @param allAssetsHistory Complete historical asset data for comprehensive map updates.
   * @param allRoutesHistory Complete historical route data for route matching operations.
   * @param applyReportFilter Callback function to apply filtering logic during user interactions.
   * @param historyUpdatesLive Boolean flag indicating real-time update mode for live data.
   * @param historyUpdatesLiveRef React ref tracking live update state for performance optimization.
   * @param isAssetSnapToRoadEnabled Boolean flag to enable/disable route matching functionality.
   */
  const initializeAssetsOnMap = useCallback(async({
    map,
    mapView,
    assets,
    setZoom,
    boundary,
    zoomLevel,
    isTracking,
    routesHistory,
    isAssetHistory,
    setClickSetting,
    hoveredEventRef,
    allAssetsHistory,
    allRoutesHistory,
    applyReportFilter,
    historyUpdatesLive,
    historyUpdatesLiveRef,
    isAssetSnapToRoadEnabled,
    isTripServicesEnabled = false
  }: InitializeAssetsOnMapProps) => {
    // No assets → clear graphics and exit
    if (!assets?.length) {
      graphicLayerRef.current?.graphics.removeAll();
      return;
    }
    
    const isFirstLoad = !layerExists(map, IN_TRIP_NODE_LAYER);
    
    // Generate primary asset map
    const {
      assetsMap,
      nodeGraphic,
      pathGraphic,
      endEventNodes
    } = generateAssetMap({
      assets,
      boundary,
      routesHistory,
      isTripServicesEnabled,
      isAssetSnapToRoadEnabled
    });

    // Handle live/tracking mode separately
    if (historyUpdatesLive || isTracking) {
      const markers = createMarker(addGeometry(assets));
      graphicLayerRef.current?.graphics.addMany(markers);
      return;
    }

    if (isFirstLoad) {
      setZoom(zoomLevel);
      mapPannedRef.current = false;

      if (!isSelectionEnabled) {
        removeLayers(map, [BACKGROUND_WORLD_LAYER]);
      }

      backgroundWorldLayerRef.current =
        getLayerById(map, BACKGROUND_WORLD_LAYER) || null as any;

      resetGraphicsLayer(setClickSetting, historyUpdatesLive, isTracking);
      startAssetHistoryColors(Object.keys(assetsMap || {}));
      createLayerRenderersByZoom(assetsMap as any, isAssetHistory);

      await createAHRLayers({
        assets,
        mapView,
        safeBind,
        boundary,
        assetsMap,
        nodeGraphic,
        pathGraphic,
        endEventNodes,
        isAssetHistory,
        setClickSetting,
        hoveredEventRef,
        applyReportFilter,
        historyUpdatesLiveRef,
        isTripServicesEnabled
      });

      preventZoomRef.current = false;
    } else {
      // ---- UPDATE FLOW ----
      const {assetsMap: allAssetsMap} = generateAssetMap({
        assets        : allAssetsHistory,
        routesHistory : allRoutesHistory,
        onlyAssetMap  : true,
        isAssetSnapToRoadEnabled,
        isTripServicesEnabled,
        boundary
      });

      // the page's own asset is given its color first, in the order it was
      // drawn, before the rest of the report is read back ...
      registerAssetHistoryColors(Object.keys(assetsMap || {}));
      registerAssetHistoryColors(Object.keys(allAssetsMap || {}));
      createLayerRenderersByZoom(allAssetsMap as any, isAssetHistory);

      updateAHRLayers({
        mapView,
        nodeGraphic,
        pathGraphic,
        endEventNodes,
        isAssetHistory,
        allAssetsHistory,
        isTripServicesEnabled,
        assetsMap : allAssetsMap as any
      });
    }
  }, [map]);

  /**
   * Dynamically assigns renderers to map layers based on the current zoom level.
   *
   * - Debounced to prevent excessive renderer updates during rapid zoom interactions.
   * - Retrieves predefined renderers for different icon sizes (Normal, Medium, Large).
   * - Updates the in-trip node layer renderer according to the current zoom level.
   * - If available, also updates the end-event node layer renderer:
   *    • Uses a distinct renderer for Normal size.
   *    • Applies offset renderers for Medium and Large sizes.
   * - Ensures all required layer references and renderers exist before applying updates.
   *
   * @param zoom - Current zoom level used to determine the appropriate renderer.
   */
  const assignLayerRenderer = useCallback(debounce(zoom => {
    const normalIconSizeRenderer = normalIconSizeRendererRef.current;
    const mediumIconSizeRenderer = mediumIconSizeRendererRef.current;
    const largeIconSizeRenderer  = largeIconSizeRendererRef.current;
      
    if (
      inTripNodeLayerRef.current &&
      normalIconSizeRenderer &&
      mediumIconSizeRenderer &&
      largeIconSizeRenderer
    ) {
      const inTripNodeLayerRenderer: RendererMapType = {
        Normal : normalIconSizeRenderer,
        Medium : mediumIconSizeRenderer,
        Large  : largeIconSizeRenderer
      };
      inTripNodeLayerRef.current.renderer = getLayerRenderer(zoom, inTripNodeLayerRenderer);
  
      if (endEventNodeLayerRef.current) {
        const endEventNodeRenderer: RendererMapType = {
          Normal : endEventNodeRendererRef.current,
          Medium : endEventNodeOffsetRendererRef.current,
          Large  : endEventNodeOffsetRendererRef.current
        };
        endEventNodeLayerRef.current.renderer = getLayerRenderer(zoom, endEventNodeRenderer);
      }
    }
  }, 300), [getLayerRenderer]);

  return {
    mapPannedRef,
    preventZoomRef,
    assetsPointsRef,
    graphicLayerRef,
    iconSizeLabelRef,
    createFeatureLayer,
    assignLayerRenderer,
    createBackgroundLayer,
    initializeAssetsOnMap,
    applyFilterToLayerViews,
    backgroundWorldLayerRef,
    endEventNodeRendererRef,
    largeIconSizeRendererRef,
    normalIconSizeRendererRef,
    mediumIconSizeRendererRef,
    endEventNodeOffsetRendererRef
  };
};

export default useAssetHistoryMapLayers;
